import React from 'react';
import { ScheduleItem, Type, Judge } from '../../../../../api/responses';
import { formatTime } from '../../utils';
import type { ColumnsType } from 'antd/es/table';
import {
  TimeContainer,
  GroupName,
  PlayersHeader,
  PlayerNumber,
  TimeBadge,
} from '../../styles';
import Icon from '../../../../assets/icons';
import ReservedJudges from '../ReservedJudges';
import {
  DesktopTableWrapper,
  JudgeWrapper,
  StyledTable,
  TextWrapper,
  PlayerRow,
  HeaderWrapper,
  JudgeLineWrapper,
} from './styles';
import { Name } from '../Judges/styles';

type TableRecord = ScheduleItem & {
  key: string | number;
  isReserveJudgesRow?: boolean;
  isPlayerOneRow?: boolean;
  gameGroupIndex?: number;
};

type ScheduleTableProps = {
  gameItems: ScheduleItem[];
  competitionType: Type;
  lang?: string;
};

const ScheduleTableDesktop = ({
  gameItems,
  competitionType,
  lang = 'ru',
}: ScheduleTableProps) => {
  const prepareTableData = (): TableRecord[] => {
    const result: TableRecord[] = [];

    gameItems.forEach((item, index) => {
      const gameGroupIndex = index;

      result.push({
        key: `${item.id}-p1`,
        ...item,
        isPlayerOneRow: true,
        gameGroupIndex,
      });

      result.push({
        key: `${item.id}-p2`,
        ...item,
        isPlayerOneRow: false,
        gameGroupIndex,
      });

      result.push({
        key: `reserve-${item.id}`,
        ...item,
        isReserveJudgesRow: true,
        gameGroupIndex,
      } as unknown as TableRecord);
    });

    return result;
  };

  const renderJudgesContent = (judges: Judge[], voterType: string) => {
    if (judges.length === 0) return;

    return judges
      .filter((judge) => judge.voter_type === voterType)
      .map((judge, index) => (
        <div key={judge?.id ?? index} style={{ marginBottom: '4px' }}>
          {judge && (
            <JudgeWrapper>
              {judge.vote_for && (
                <PlayerNumber $playerNumber={judge.vote_for}>
                  {judge.vote_for}
                </PlayerNumber>
              )}
              <Name>
                {judge.last_name} {judge.first_name}
              </Name>
            </JudgeWrapper>
          )}
        </div>
      ));
  };

  // Функция для определения класса строки
  const getRowClassName = (record: unknown) => {
    const tableRecord = record as TableRecord;
    // Добавляем класс для четных групп (каждая группа = 3 строки)
    if (
      tableRecord.gameGroupIndex !== undefined &&
      tableRecord.gameGroupIndex % 2 === 1
    ) {
      return `even-game-group ${competitionType}-even-group`;
    }
    return '';
  };

  // Все колонки в одном массиве с условиями видимости
  const gameColumns: ColumnsType<TableRecord> = [
    {
      title: lang === 'ru' ? 'Время' : 'Time',
      dataIndex: 'time',
      key: 'time',
      width: '100px',
      render: (text: string, record: TableRecord) => {
        const content = (
          <TimeContainer>
            <TimeBadge>
              {formatTime(record.start_time)} - {formatTime(record.end_time)}
            </TimeBadge>
            {record.group_name && (
              <GroupName>
                {lang === 'ru' ? 'Группа' : 'Group'} {record.group_name}
              </GroupName>
            )}
          </TimeContainer>
        );

        if (record.isReserveJudgesRow) {
          return {
            children: null,
            props: { colSpan: 7 },
          };
        }

        return {
          children: content,
          props: {
            rowSpan: record.isPlayerOneRow ? 2 : 0,
          },
        };
      },
    },
    {
      title: '№',
      dataIndex: 'game_number',
      key: 'game_number',
      width: '32px',
      render: (text: string, record: TableRecord) => {
        if (record.isReserveJudgesRow) {
          return {
            children: null,
            props: { colSpan: 0 },
          };
        }

        const content = record.game?.game_number;
        return {
          children: <TextWrapper>{content}</TextWrapper>,
          props: {
            rowSpan: record.isPlayerOneRow ? 2 : 0,
          },
        };
      },
    },
    {
      title: lang === 'ru' ? 'Сит.' : 'Case',
      dataIndex: 'case_number',
      key: 'case_number',
      width: '60px',
      render: (text: string, record: TableRecord) => {
        if (record.isReserveJudgesRow) {
          return {
            children: null,
            props: { colSpan: 0 },
          };
        }

        const content = record.game?.case_number
          ? `${record.game.case_number}`
          : '';
        return {
          children: <TextWrapper>{content}</TextWrapper>,
          props: {
            rowSpan: record.isPlayerOneRow ? 2 : 0,
          },
        };
      },
    },
    {
      title: (
        <PlayersHeader>
          <Icon variant='cube' />
          {lang === 'ru' ? 'Игроки' : 'Players'}
        </PlayersHeader>
      ),
      dataIndex: 'players',
      key: 'players',
      width: '240px',
      render: (text: string, record: TableRecord) => {
        if (record.isReserveJudgesRow) {
          return {
            children: null,
            props: { colSpan: 0 },
          };
        }

        if (record.isPlayerOneRow) {
          const player1 = record.game?.player_1;
          const player1Name = player1
            ? `${player1.last_name} ${player1.first_name}`
            : '';
          return (
            <>
              {player1Name && (
                <PlayerRow $hasBorder={false}>
                  <PlayerNumber $playerNumber={1}>1</PlayerNumber>
                  {player1Name}
                </PlayerRow>
              )}
            </>
          );
        } else {
          const player2 = record.game?.player_2;
          const player2Name = player2
            ? `${player2.last_name} ${player2.first_name}`
            : '';
          return (
            <>
              {player2Name && (
                <PlayerRow $hasBorder={false}>
                  <PlayerNumber $playerNumber={2}>2</PlayerNumber>
                  {player2Name}
                </PlayerRow>
              )}
            </>
          );
        }
      },
    },
    {
      title: lang === 'ru' ? 'Счёт' : 'Score',
      dataIndex: 'score',
      key: 'score',
      width: '80px',
      render: (text: string, record: TableRecord) => {
        if (record.isReserveJudgesRow) {
          return {
            children: null,
            props: { colSpan: 0 },
          };
        }

        const score = record.game?.score || '';
        const scorePart = record.isPlayerOneRow
          ? score.split(':')[0]
          : score.split(':')[1];
        return (
          <TextWrapper>{score && score !== '0:0' ? scorePart : ''}</TextWrapper>
        );
      },
    },
    {
      title: lang === 'ru' ? 'Рейтинг до начала поединка' : 'Initial rating',
      dataIndex: 'rating_before',
      key: 'rating_before',
      width: '100px',
      render: (text: string, record: TableRecord) => {
        if (record.isReserveJudgesRow) {
          return {
            children: null,
            props: { colSpan: 0 },
          };
        }

        const rating = record.game?.rating_before;
        const ratingPart = record.isPlayerOneRow
          ? rating?.player_1
          : rating?.player_2;
        return <TextWrapper>{ratingPart || ''}</TextWrapper>;
      },
    },
    {
      title: lang === 'ru' ? 'Рейтинг по итогу поединка' : 'Definitive rating',
      dataIndex: 'rating_after',
      key: 'rating_after',
      width: '100px',
      render: (text: string, record: TableRecord) => {
        if (record.isReserveJudgesRow) {
          return {
            children: null,
            props: { colSpan: 0 },
          };
        }

        const rating = record.game?.rating_after;
        const ratingPart = record.isPlayerOneRow
          ? rating?.player_1
          : rating?.player_2;
        return <TextWrapper>{ratingPart || ''}</TextWrapper>;
      },
    },
    // Колонка для quick game
    {
      title:
        lang === 'ru'
          ? 'Судьи "Отправляющие на переговоры"'
          : 'Negotiator voters',
      dataIndex: 'negotiator_judges',
      key: 'negotiator_judges',
      hidden: competitionType !== 'quick',
      render: (text: string, record: TableRecord) => {
        if (record.isReserveJudgesRow) {
          // Для строки резервных судей в quick game
          return {
            children: record.game?.reserve_judge ? (
              <ReservedJudges judges={record.game.reserve_judge} lang={lang} />
            ) : null,
            props: {
              colSpan: 1,
            },
          };
        }

        const judges = record.game?.judges || [];
        const negotiatorJudges = judges.filter(
          (judge: Judge) => judge.voter_type === 'negotiator'
        );

        if (negotiatorJudges.length === 0) {
          return {
            children: '',
            props: {
              rowSpan: record.isPlayerOneRow ? 2 : 0,
            },
          };
        }

        const content = renderJudgesContent(judges, 'negotiator');

        return {
          children: content,
          props: {
            rowSpan: record.isPlayerOneRow ? 2 : 0,
          },
        };
      },
    },
    // Колонки для classic и team games
    {
      title: lang === 'ru' ? 'СУДЬИ' : 'VOTERS',
      hidden: competitionType !== 'classic' && competitionType !== 'team',
      children: [
        {
          title: lang === 'ru' ? 'Нанимающиеся на работу' : 'Employees',
          dataIndex: 'employee_judges',
          key: 'employee_judges',
          render: (text: string, record: TableRecord) => {
            if (record.isReserveJudgesRow) {
              // Для строки резервных судей - показываем компонент только в первой колонке группы
              return {
                children: record.game?.reserve_judge ? (
                  <ReservedJudges
                    judges={record.game.reserve_judge}
                    lang={lang}
                  />
                ) : null,
                props: {
                  colSpan: 3,
                },
              };
            }

            const judges = record.game?.judges || [];
            const employeeJudges = judges.filter(
              (judge: Judge) => judge.voter_type === 'employee'
            );

            if (employeeJudges.length === 0) {
              return {
                children: '',
                props: {
                  rowSpan: record.isPlayerOneRow ? 2 : 0,
                },
              };
            }

            const content = renderJudgesContent(judges, 'employee');

            return {
              children: content,
              props: {
                rowSpan: record.isPlayerOneRow ? 2 : 0,
              },
            };
          },
        },
        {
          title:
            lang === 'ru' ? (
              <HeaderWrapper>Отправляющие на переговоры</HeaderWrapper>
            ) : (
              'Negotiators'
            ),
          dataIndex: 'negotiator_judges_classic',
          key: 'negotiator_judges_classic',
          render: (text: string, record: TableRecord) => {
            if (record.isReserveJudgesRow) {
              // Для строки резервных судей - скрываем остальные колонки группы
              return {
                children: null,
                props: {
                  colSpan: 0,
                },
              };
            }

            const judges = record.game?.judges || [];
            const negotiatorJudges = judges.filter(
              (judge: Judge) => judge.voter_type === 'negotiator'
            );

            if (negotiatorJudges.length === 0) {
              return {
                children: <JudgeLineWrapper>{null}</JudgeLineWrapper>,
                props: {
                  rowSpan: record.isPlayerOneRow ? 2 : 0,
                },
              };
            }

            const content = renderJudgesContent(judges, 'negotiator');

            return {
              children: <JudgeLineWrapper>{content}</JudgeLineWrapper>,
              props: {
                rowSpan: record.isPlayerOneRow ? 2 : 0,
              },
            };
          },
        },
        {
          title: lang === 'ru' ? 'Доверяющие собственность' : 'Founders',
          dataIndex: 'founder_judges',
          key: 'founder_judges',
          render: (text: string, record: TableRecord) => {
            if (record.isReserveJudgesRow) {
              // Для строки резервных судей - скрываем остальные колонки группы
              return {
                children: null,
                props: {
                  colSpan: 0,
                },
              };
            }

            const judges = record.game?.judges || [];
            const founderJudges = judges.filter(
              (judge: Judge) => judge.voter_type === 'founder'
            );

            if (founderJudges.length === 0) {
              return {
                children: '',
                props: {
                  rowSpan: record.isPlayerOneRow ? 2 : 0,
                },
              };
            }

            const content = renderJudgesContent(judges, 'founder');

            return {
              children: content,
              props: {
                rowSpan: record.isPlayerOneRow ? 2 : 0,
              },
            };
          },
        },
      ],
    },
  ];

  const visibleColumns = gameColumns.filter((column) => !column.hidden);

  const tableData = prepareTableData();

  return (
    <DesktopTableWrapper>
      <StyledTable
        $competitionType={competitionType}
        columns={visibleColumns as any}
        dataSource={tableData}
        pagination={false}
        size='small'
        scroll={{ x: true }}
        rowKey={(record: any) => record.key}
        rowClassName={getRowClassName}
      />
    </DesktopTableWrapper>
  );
};

export default ScheduleTableDesktop;
